import { useCallback, useEffect, useState } from "react";
import AutoPublish from "./AutoPublish";
import Documents from "./Documents";
import Inbound from "./Inbound";
import InboundAddress from "./InboundAddress";
import Published from "./Published";
import Unanswered from "./Unanswered";
import Upload from "./Upload";
import { exportAll, getInboundAddress, getSettings, listDocuments, listInbound,
         listPublished, startSession, unanswered as fetchUnanswered } from "./api";
import { schoolOf, signIn, signOutUser, watchUser } from "./firebase";

/**
 * Portalens rubrik.
 *
 * Skolans namn kommer ur `schools.name` via /admin/documents — aldrig
 * hårdkodat. AA-8 gäller rubriken lika mycket som domänlistan: det ligger redan
 * två skolor i basen, och nästa ska inte behöva en egen build för att få rätt
 * namn i huvudet.
 *
 * **Utan skola står det "Skoladmin", och det är ett val.** Före inloggningen,
 * för den som nekas, och medan dokumentlistan hämtas finns ingen skola att visa
 * — vi vet inte vilken det är, för det är claimen som avgör. Att då skriva
 * portalens roll är ärligare än en tom sträng eller en gissning, och rubriken
 * byter inte höjd när namnet kommer.
 */
function Heading({ schoolName }) {
  return (
    <div className="title">
      <h1>
        Ask Aerro
        <span className="sep" aria-hidden="true"> · </span>
        <span className="scope">{schoolName || "Skoladmin"}</span>
      </h1>
      {/* Rollen får en egen rad när skolnamnet tagit dess plats i h1:an —
          annars försvinner den upplysningen helt för den som är inloggad. */}
      {schoolName && <p className="role">Skoladmin</p>}
    </div>
  );
}


export default function App() {
  const [user, setUser] = useState(undefined);   // undefined = vet inte än
  const [school, setSchool] = useState(null);
  const [docs, setDocs] = useState({ documents: [], jobs: [] });
  const [schoolName, setSchoolName] = useState(null);
  const [unans, setUnans] = useState(null);
  // Granskningskön. `null` tills den hämtats — vyn ritar då ingenting alls,
  // i stället för ett tomt "Inga utskick väntar" som skulle vara en
  // osanning under den sekund hämtningen tar.
  const [inbound, setInbound] = useState(null);
  const [published, setPublished] = useState(null);
  const [settings, setSettings] = useState(null);
  const [address, setAddress] = useState(null);
  // Fliken ligger i adressfältet, så att en omladdning — eller en länk till en
  // kollega — landar på samma ställe. `utskick` först: det som väntar på ett
  // beslut är det enda på sidan som har en deadline.
  const [tab, setTab] = useState(
    () => window.location.hash.replace("#", "") || "utskick");
  const [error, setError] = useState(null);
  // Backendens besked när ett anrop nekas. Claimen kan finnas i tokenen och
  // anropet ändå nekas — av domänfiltret, eller för att claimen pekar på en
  // skola som inte finns — och då är backendens mening det enda som säger
  // vilket av dem det är.
  const [denied, setDenied] = useState(null);
  // Backendfel vid behörighetsuppslaget, till skillnad från ett besked om att
  // behörighet saknas. De två såg identiska ut fram till 2026-09-01 — catch-
  // blocket nedan svalde felet och användaren fick "ingen behörighet" — och
  // skillnaden kostade en timmes felsökning i fel ände. Ett nekande är ett
  // svar; ett fel är att frågan aldrig blev besvarad.
  const [sessionError, setSessionError] = useState(null);

  useEffect(() => watchUser(async (next) => {
    setUser(next);
    if (!next) {
      setSchool(null);
      setSchoolName(null);
      return;
    }

    setSessionError(null);

    let claimed = await schoolOf(next);
    if (!claimed) {
      // Ingen claim i tokenen betyder inte att behörighet saknas. Den som
      // vitlistats men aldrig loggat in HAR ingen claim än — den sätts av
      // /admin/session, och det här anropet är det som utlöser det.
      try {
        const result = await startSession(next);
        // Loggas alltid, båda utfallen. Backenden svarar med flit likadant på
        // "står inte på listan" och "fel domän" — den ska inte gå att använda
        // som uppslagsverk — så konsolen är det enda stället där den som
        // felsöker ser vad servern faktiskt sa.
        console.info("[aerro] /admin/session:", result);
        // Force-refresh, annars läser vi tillbaka samma token vi just skickade
        // och ser fortfarande ingen claim. Det felet ser ut precis som att
        // behörigheten aldrig gavs.
        if (result.granted) claimed = await schoolOf(next, true);
      } catch (caught) {
        // INTE tyst. Att svälja det här gjorde ett serverfel oskiljbart från
        // ett nekande, och sidan påstod att användaren saknade behörighet när
        // sanningen var att vi aldrig fick veta.
        console.error("[aerro] /admin/session misslyckades:", caught);
        setSessionError(caught);
      }
    }
    setSchool(claimed);
  }), []);

  const reload = useCallback(async () => {
    if (!user || !school) return;
    try {
      const listing = await listDocuments(user);
      setDocs(listing);
      // Namnet kommer med dokumentlistan. Äldre backend utan fältet ger
      // undefined, och rubriken faller då tillbaka på "Skoladmin" i stället för
      // att visa "undefined" — se `Heading`.
      setSchoolName(listing.school?.name || null);
      setUnans(await fetchUnanswered(user));
      setInbound(await listInbound(user));
      setPublished(await listPublished(user));
      setSettings(await getSettings(user));
      setAddress(await getInboundAddress(user));
      setError(null);
      setDenied(null);
    } catch (caught) {
      if (caught.status === 403) setDenied(caught.message);
      else setError(caught.message);
    }
  }, [user, school]);

  useEffect(() => { reload(); }, [reload]);

  // Fliken följer rubriken. `index.html` bär grundtiteln — den som syns innan
  // React monterat och för den som aldrig får en skola — och den här skriver
  // över den så fort vi vet vilken skola det gäller. Statisk HTML kan inte veta
  // det; skolan står i claimen.
  useEffect(() => {
    document.title = schoolName ? `Ask Aerro · ${schoolName}` : "Ask Aerro · Skoladmin";
  }, [schoolName]);

  async function download() {
    const payload = await exportAll(user);
    const blob = new Blob([JSON.stringify(payload, null, 2)],
                          { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `aerro-export-${school}-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  if (user === undefined) return <main className="page"><p>Laddar…</p></main>;

  if (!user) {
    return (
      <main className="page">
        <Heading />
        <p>Logga in med ditt skolkonto.</p>
        <button onClick={signIn}>Logga in med Google</button>
      </main>
    );
  }

  if (sessionError) {
    // Eget läge, skilt från "ingen behörighet". Vi VET inte om behörighet
    // saknas — uppslaget gick aldrig igenom — och att påstå något annat
    // skickar den som felsöker till fel ände. Texten säger vad som hände och
    // vad det inte betyder.
    return (
      <main className="page">
        <Heading />
        <p>
          Kunde inte kontrollera din behörighet — Aerro svarade inte. Det är ett
          tekniskt fel hos oss, <strong>inte</strong> ett besked om att du
          saknar behörighet. Ladda om sidan om en stund; kvarstår det, hör av
          dig till Aerro.
        </p>
        <p className="hint">
          Inloggad som {user.email}
          {sessionError.status ? ` · HTTP ${sessionError.status}` : ""}
          {sessionError.message ? ` · ${sessionError.message}` : ""}
        </p>
        <button onClick={signOutUser}>Logga ut</button>
      </main>
    );
  }

  if (!school || denied) {
    // Två olika lägen med samma utseende, och det är avsiktligt: för den som
    // står här är skillnaden mellan "ingen claim i tokenen" och "claim men
    // nekad av backenden" ointressant. Det som betyder något är VARFÖR, och
    // den meningen kommer från backenden när den finns.
    //
    // Ett BACKENDFEL hör däremot inte hemma här — det fångas ovan. Det som når
    // hit är ett svar, inte ett uteblivet svar.
    //
    // `denied` sätts av ett 403 med ett meddelande. Utan det faller vi tillbaka
    // på texten för det vanligaste fallet — en anställd som väntar på sin
    // behörighet, inte någon som försöker ta sig in.
    return (
      <main className="page">
        <Heading />
        <p>
          {denied || (
            <>
              Kontot <strong>{user.email}</strong> har ingen behörighet till
              adminportalen. Behörigheten sätts manuellt av Aerro — hör av dig
              så ordnar vi det.
            </>
          )}
        </p>
        <p className="hint">Inloggad som {user.email}</p>
        <button onClick={signOutUser}>Logga ut</button>
      </main>
    );
  }

  return (
    <main className="page">
      <header>
        <Heading schoolName={schoolName} />
        <div className="who">
          <span>{user.email}</span>
          <button onClick={signOutUser}>Logga ut</button>
        </div>
      </header>

      {error && <p className="error">{error}</p>}

      {/* Flikar i stället för en lång sida. Uppladdade filer och skolans
          utskick är två olika arbeten — det ena görs när någon har en fil, det
          andra när något kommit in — och att lägga dem under varandra gjorde
          att granskningskön hamnade ovanför en uppladdningsruta man rullade
          förbi varje gång. Antalet i fliken är det som säger att något väntar. */}
      <nav className="tabs" role="tablist">
        {[["utskick", "Utskick", inbound?.messages?.length || 0],
          ["dokument", "Dokument", 0],
          ["fragor", "Obesvarade frågor", unans?.unanswered?.length || 0]].map(
          ([id, etikett, antal]) => (
            <button key={id} role="tab" aria-selected={tab === id}
                    className={tab === id ? "tab active" : "tab"}
                    onClick={() => { setTab(id); window.location.hash = id; }}>
              {etikett}
              {antal > 0 && <span className="badge">{antal}</span>}
            </button>
          ))}
      </nav>

      {tab === "utskick" && (
        <>
          {/* Adressen och reglaget först: det är de två sakerna skolan STÄLLER
              IN. Kön och det publicerade är vad som händer sedan. */}
          <InboundAddress data={address} />
          <AutoPublish user={user} settings={settings} onChange={reload} />
          {/* Kön först: det som väntar på ett beslut är det enda på sidan som
              har en deadline — AA-5.3:s frist tömmer ett obeslutat utskick
              efter 30 dagar, och ett utskick som ingen hann läsa är ett utskick
              skolan trodde kom fram. */}
          <Inbound user={user} data={inbound} onChange={reload} />
          <Published data={published} />
        </>
      )}

      {tab === "dokument" && (
        <>
          <Upload user={user} schoolId={school} onDone={reload} />
          <Documents user={user} documents={docs.documents} jobs={docs.jobs}
                     onChange={reload} />
          <section className="card">
            <h2>Export</h2>
            <p className="hint">
              Allt skolan laddat upp, som en JSON-fil: dokumentens metadata, den
              indexerade texten och revisionsloggen. Originalfilerna ligger kvar
              i lagringen och listas med sin sökväg.
            </p>
            <button onClick={download}>Ladda ner export</button>
          </section>
        </>
      )}

      {tab === "fragor" && <Unanswered data={unans} />}
    </main>
  );
}
